import * as React from 'react'
import { Field } from './Field'
import { FieldStatus } from './FieldStatus'
import { styled } from 'next-yak'
import * as S from '../../../styles'
import * as colors from '../../../constants/colors'
import {
  ControlledCheckbox,
  ControlledCheckboxWrapper,
  checkedCheckboxCheck,
  focusedCheckboxCheck,
} from '../checkbox/Checkbox'

export const StyledCheckboxField = styled(Field)`
  ${S.field.Status} {
    width: 100%;
  }

  ${S.field.Description} {
    color: ${colors.TEXT_TERTIARY};
  }
`

export const CheckboxFieldWrapper = styled.div`
  position: relative;
  padding-left: 28px;

  ${S.input.HiddenFocusableInput} {
    left: 0;
    width: 18px;
    height: 18px;
    top: 2px;
  }

  ${ControlledCheckboxWrapper} {
    order: 1;
    position: absolute;
    top: 2px;
    left: 0;
  }

  ${S.field.Status} {
    width: 100%;
  }

  ${S.input.HiddenFocusableInput}:disabled {
    cursor: not-allowed;
  }

  ${S.input.HiddenFocusableInput}:focus + ${StyledCheckboxField} {
    ${focusedCheckboxCheck};
  }

  ${S.input.HiddenFocusableInput}:checked + ${StyledCheckboxField} {
    ${checkedCheckboxCheck};
  }

  ${S.input.HiddenFocusableInput}:disabled + ${StyledCheckboxField} {
    ${S.field.Title}, ${S.field.Description} {
      color: ${colors.PRIMARY_SAND_700};
      cursor: not-allowed;
    }
  }
`

export type CheckboxFieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  id?: string
  description?: React.ReactNode
  label?: React.ReactNode
  error?: React.ReactNode
  success?: React.ReactNode
  format?: 'wide'
}

/** will forward any other props to the underlying `<input>` */
function CheckboxFieldComponent({
  className,
  label,
  description,
  error,
  success,
  id,
  format,
  ...inputProps
}: CheckboxFieldProps): React.ReactElement<CheckboxFieldProps> {
  const stableId = React.useId()

  return (
    <CheckboxFieldWrapper className={className}>
      <S.input.HiddenFocusableInput type="checkbox" {...inputProps} id={id ?? stableId} />
      <StyledCheckboxField format={format} htmlFor={id ?? stableId} label={label} description={description}>
        <ControlledCheckbox {...inputProps} error={error} disabled={inputProps.disabled} />
        <FieldStatus error={error} success={success} />
      </StyledCheckboxField>
    </CheckboxFieldWrapper>
  )
}

export const CheckboxField = styled(CheckboxFieldComponent)<CheckboxFieldProps>`
  ${S.field.Title} {
    ${S.copy.styles.S.regular};
    margin-top: 0;
  }
`
CheckboxField.displayName = 'CheckboxField'
